import { useState, useEffect, useCallback, useRef } from 'react'
import api from '@/services/api'

export interface UseFetchResult<T> {
  data: T | null
  loading: boolean
  error: string | null
  refetch: () => void
}

type FetchInput<T> = string | (() => Promise<T>)

/**
 * Versatile data fetching hook with abort controller support
 *
 * @example
 * // Simple GET request
 * const { data } = useFetch<Article[]>('/articles')
 *
 * @example
 * // Custom API call
 * const { data } = useFetch(() => api.post('/articles', body))
 *
 * @example
 * // With refetch
 * const { data, refetch } = useFetch<User>('/auth/me')
 * // Later: refetch()
 */
export function useFetch<T>(
  input: FetchInput<T>
): UseFetchResult<T> {
  const [data, setData] = useState<T | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const abortControllerRef = useRef<AbortController | null>(null)

  const fetchData = useCallback(async () => {
    // Cancel previous request if still running
    if (abortControllerRef.current) {
      abortControllerRef.current.abort()
    }

    // Create new abort controller
    abortControllerRef.current = new AbortController()
    const signal = abortControllerRef.current.signal

    try {
      setLoading(true)
      setError(null)

      let result: T
      if (typeof input === 'string') {
        // Simple GET request
        result = await api.get<T>(input)
      } else {
        // Custom function
        result = await input()
      }

      // Only update state if not aborted
      if (!signal.aborted) {
        setData(result)
      }
    } catch (err) {
      // Ignore abort errors
      if (err instanceof Error && err.name === 'AbortError') {
        return
      }

      if (!signal.aborted) {
        setError(err instanceof Error ? err.message : 'Une erreur est survenue')
      }
    } finally {
      if (!signal.aborted) {
        setLoading(false)
      }
    }
  }, [input])

  useEffect(() => {
    fetchData()

    // Cleanup: abort fetch on unmount
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort()
      }
    }
  }, [fetchData])

  return { data, loading, error, refetch: fetchData }
}

// Export alias for backward compatibility (if useApi was used)
export { useFetch as useApi }
